import { useEffect, useMemo, useState } from "react";
import { Alert, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import PercentRounded from "@mui/icons-material/PercentRounded";
import TrendingDownRounded from "@mui/icons-material/TrendingDownRounded";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import AgenteContabilidadeShell from "../components/AgenteContabilidadeShell";
import ChartCard from "../components/ChartCard";
import EmpreendimentosIntersecaoNotice from "../components/EmpreendimentosIntersecaoNotice";
import FilterSelect from "../components/FilterSelect";
import KpiGrid from "../components/KpiGrid";
import MonthFallbackNotice from "../components/MonthFallbackNotice";
import PeriodSelector from "../components/PeriodSelector";
import { useAgenteContabilidadeStore } from "../hooks/useAgenteContabilidadeStore";
import { usePageEmpresaSelection } from "../hooks/usePageEmpresaSelection";
import { usePageMonthSelection } from "../hooks/usePageMonthSelection";
import { logContabilidadeApiError, obterVisaoGeral } from "../services/agenteContabilidadeApiService";
import {
  buildEmpresaFilterParams,
  buildEmpreendimentoOptions,
  findEmpreendimentoByFilterValue,
  getEmpreendimentoFilterValue,
  normalizeEmpreendimentoContext,
  TODOS_EMPREENDIMENTOS_VALUE,
} from "../utils/empreendimentoSelector";
import {
  getIntersecaoForMonth,
  normalizeIntersecaoList,
  normalizeIntersecaoPorMes,
  resolveActiveMonth,
} from "../utils/intersecaoEmpreendimentos";
import { colors } from "../constants/designTokens";
import { formatCurrency, formatPercent } from "../utils/formatters";
import {
  readSelectedDreReference,
  readSelectedDreSimulation,
  resolveDreSimulationForMonth,
  resolveInternalEmpresaId,
} from "../utils/dreSimulationSession";

const getFirstValue = (item, keys, fallback = 0) => {
  for (const key of keys) {
    if (item?.[key] !== undefined && item?.[key] !== null) {
      return item[key];
    }
  }

  return fallback;
};

const toNumber = (value) => {
  const numberValue = Number(value || 0);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

function getEmpresaId(item) {
  return getEmpreendimentoFilterValue(item, "codigoEmpresa");
}

function normalizeVisaoGeralItem(item) {
  return {
    idClienteEmpresa: getEmpresaId(item),
    nomeEmpresa: getFirstValue(item, ["nomeEmpresa", "nome_empresa", "empresaArquivo", "empresa_arquivo"], ""),
    mes_ano: getFirstValue(item, ["mes_ano", "mesAno", "competencia", "mes"], ""),
    poc: toNumber(getFirstValue(item, ["poc", "pocExecucao", "poc_execucao"])),
    pct_vendido: toNumber(getFirstValue(item, ["pct_vendido", "percentualVendido", "percentual_vendido"])),
    vgv: toNumber(getFirstValue(item, ["vgv", "vgvContratado", "vgv_contratado"])),
    receita_apropriada: toNumber(getFirstValue(item, ["receita_apropriada", "receitaApropriada"])),
    custo_apropriado: toNumber(getFirstValue(item, ["custo_apropriado", "custoApropriado"])),
    recebimento_acumulado: toNumber(getFirstValue(item, ["recebimento_acumulado", "recebimentoAcumulado"])),
    tendencia_margem_liquida: toNumber(getFirstValue(item, [
      "tendencia_margem_liquida",
      "tendenciaMargemLiquida",
      "margemTendencia",
      "margem_tendencia",
    ])),
  };
}

function normalizeEmpresaItem(item) {
  const idClienteEmpresa = getEmpresaId(item);
  const nomeEmpresa = getFirstValue(item, ["nomeEmpresa", "nome_empresa", "empresaArquivo", "empresa_arquivo"], "");

  if (!idClienteEmpresa) {
    return null;
  }

  return {
    value: idClienteEmpresa,
    label: nomeEmpresa || `Empresa ${idClienteEmpresa}`,
  };
}

function normalizeVisaoGeralResponse(payload) {
  if (!payload) {
    return null;
  }

  const sourceTimeline =
    (Array.isArray(payload) && payload) ||
    payload.timeline ||
    payload.grafico ||
    payload.registros ||
    payload.items ||
    payload.data ||
    [];

  if (!Array.isArray(sourceTimeline)) {
    return null;
  }

  const timeline = sourceTimeline.map(normalizeVisaoGeralItem).filter((item) => item.mes_ano);
  const empresasSource = Array.isArray(payload.empresas) ? payload.empresas : [];
  const empresas = empresasSource
    .map(normalizeEmpresaItem)
    .filter(Boolean);
  const empresasFromTimeline = timeline
    .map(normalizeEmpresaItem)
    .filter(Boolean);
  const months = Array.isArray(payload.meses)
    ? payload.meses
    : Array.isArray(payload.months)
    ? payload.months
    : timeline.map((item) => item.mes_ano);
  const empreendimentosIntersecao =
    payload.empreendimentosIntersecao ||
    payload.empreendimentos_intersecao ||
    payload.intersecaoEmpreendimentos ||
    payload.intersecao_empreendimentos ||
    [];

  return {
    empresas: empresas.length ? empresas : empresasFromTimeline,
    months,
    timeline,
    empreendimentosIntersecao: normalizeIntersecaoList(empreendimentosIntersecao),
    empreendimentosIntersecaoPorMes: normalizeIntersecaoPorMes(payload),
  };
}

function consolidateTimelineByMonth(timeline = []) {
  const byMonth = new Map();

  timeline.forEach((item) => {
    if (!item?.mes_ano) {
      return;
    }

    const current = byMonth.get(item.mes_ano) || {
      mes_ano: item.mes_ano,
      poc: 0,
      pct_vendido: 0,
      vgv: 0,
      receita_apropriada: 0,
      custo_apropriado: 0,
      recebimento_acumulado: 0,
      tendencia_margem_liquida: 0,
      quantidade: 0,
    };

    current.poc += toNumber(item.poc);
    current.pct_vendido += toNumber(item.pct_vendido);
    current.vgv += toNumber(item.vgv);
    current.receita_apropriada += toNumber(item.receita_apropriada);
    current.custo_apropriado += toNumber(item.custo_apropriado);
    current.recebimento_acumulado += toNumber(item.recebimento_acumulado);
    current.tendencia_margem_liquida += toNumber(item.tendencia_margem_liquida);
    current.quantidade += 1;
    byMonth.set(item.mes_ano, current);
  });

  return Array.from(byMonth.values()).map((item) => {
    const quantidade = item.quantidade || 1;
    return {
      mes_ano: item.mes_ano,
      poc: item.poc / quantidade,
      pct_vendido: item.pct_vendido / quantidade,
      vgv: item.vgv,
      receita_apropriada: item.receita_apropriada,
      custo_apropriado: item.custo_apropriado,
      recebimento_acumulado: item.recebimento_acumulado,
      tendencia_margem_liquida: item.tendencia_margem_liquida / quantidade,
    };
  });
}

export default function VisaoGeralPage() {
  const { tenant, state, actions } = useAgenteContabilidadeStore();
  const [selectedMonth, setSelectedMonth] = usePageMonthSelection("visao-geral");
  const [apiAvailableMonths, setApiAvailableMonths] = useState([]);
  const [apiVisaoGeralData, setApiVisaoGeralData] = useState(null);
  const [apiStatus, setApiStatus] = useState("idle");

  const backendEmpresas = useMemo(
    () => state.backendCatalog?.empreendimentos || [],
    [state.backendCatalog?.empreendimentos]
  );
  const backendCatalogSyncKey = state.backendCatalog?.lastSyncAt || "";
  const hasBackendEmpresas = backendEmpresas.length > 0;
  const empresaOptions = useMemo(
    () => buildEmpreendimentoOptions(backendEmpresas, { valueMode: "codigoEmpresa" }),
    [backendEmpresas]
  );
  const [selectedEmpresa, setSelectedEmpresa] = usePageEmpresaSelection(
    "visao-geral",
    empresaOptions
  );
  const selectedEmpresaInfo = useMemo(
    () => backendEmpresas.find((item) => getEmpresaId(item) === selectedEmpresa) || null,
    [backendEmpresas, selectedEmpresa]
  );
  const isTodosEmpreendimentos = selectedEmpresa === TODOS_EMPREENDIMENTOS_VALUE;

  useEffect(() => {
    actions.refreshBackendCatalog().catch(() => {});
  }, [actions]);

  useEffect(() => {
    let active = true;

    async function fetchVisaoGeral() {
      if (!backendCatalogSyncKey) {
        setApiStatus("loading");
        return;
      }

      if (!hasBackendEmpresas) {
        setApiVisaoGeralData(null);
        setApiStatus("empty");
        return;
      }

      if (!selectedEmpresa) {
        setApiStatus("loading");
        return;
      }

      setApiStatus("loading");

      try {
        const selectedEmpresaId = selectedEmpresaInfo ? getEmpresaId(selectedEmpresaInfo) : selectedEmpresa;
        const params = buildEmpresaFilterParams(selectedEmpresaId, "idClienteEmpresa");
        const payload = await obterVisaoGeral(params);
        const normalizedData = normalizeVisaoGeralResponse(payload);

        if (!active) return;

        if (normalizedData?.timeline?.length) {
          setApiAvailableMonths(normalizedData.months);
          setApiVisaoGeralData(normalizedData);
          setApiStatus("success");
        } else {
          setApiVisaoGeralData(null);
          setApiStatus("empty");
        }
      } catch (error) {
        if (!active) return;
        logContabilidadeApiError("visao-geral", error);
        setApiStatus("error");
      }
    }

    fetchVisaoGeral();

    return () => {
      active = false;
    };
  }, [
    backendCatalogSyncKey,
    hasBackendEmpresas,
    selectedEmpresa,
    selectedEmpresaInfo,
  ]);

  const usingApiData = Boolean(apiVisaoGeralData?.timeline?.length);
  const timeline = useMemo(
    () => (usingApiData ? apiVisaoGeralData.timeline : []),
    [apiVisaoGeralData, usingApiData]
  );
  const visibleTimeline = useMemo(
    () => (isTodosEmpreendimentos ? consolidateTimelineByMonth(timeline) : timeline),
    [isTodosEmpreendimentos, timeline]
  );
  const months = usingApiData && apiAvailableMonths.length
    ? apiAvailableMonths
    : visibleTimeline.map((item) => item.mes_ano);

  const activeMonth = resolveActiveMonth(months, selectedMonth);

  const currentSnapshot = visibleTimeline.find((item) => item.mes_ano === activeMonth) || visibleTimeline[visibleTimeline.length - 1];
  const intersecaoEmpreendimentos = getIntersecaoForMonth(apiVisaoGeralData, activeMonth);
  const selectedEmpreendimentoContext = isTodosEmpreendimentos
    ? { filtro: TODOS_EMPREENDIMENTOS_VALUE, nomeEmpresa: "Todos Empreendimentos" }
    : normalizeEmpreendimentoContext(findEmpreendimentoByFilterValue(backendEmpresas, selectedEmpresa, "codigoEmpresa"));
  const selectedInternalEmpresaId = resolveInternalEmpresaId(
    backendEmpresas,
    selectedEmpresa
  );
  const dreReference = isTodosEmpreendimentos
    ? null
    : readSelectedDreReference(
        tenant.clientId,
        selectedInternalEmpresaId
      );
  const monthlySimulation = isTodosEmpreendimentos
    ? null
    : readSelectedDreSimulation(
        tenant.clientId,
        selectedInternalEmpresaId,
        activeMonth
      );
  const hasMonthlySimulation = Boolean(
    dreReference?.id &&
    monthlySimulation?.id &&
    String(monthlySimulation.id) === String(dreReference.id)
  );
  const selectedViabilidadeSimulation = isTodosEmpreendimentos ? null : dreReference;
  const selectedMonthlySimulation = isTodosEmpreendimentos
    ? null
    : hasMonthlySimulation
      ? resolveDreSimulationForMonth(dreReference, monthlySimulation)
      : null;
  const margemTendenciaSimulacao = hasMonthlySimulation
    ? selectedMonthlySimulation?.indicadores?.margemTendencia
    : null;
  const tendenciaMargemLiquidaVisivel =
    margemTendenciaSimulacao !== undefined && margemTendenciaSimulacao !== null
      ? toNumber(margemTendenciaSimulacao)
      : toNumber(currentSnapshot?.tendencia_margem_liquida);
  function handleEmpresaChange(value) {
    setSelectedMonth((current) => current || activeMonth);
    setSelectedEmpresa(value || "");
    setApiAvailableMonths([]);
  }

  const overviewMetrics = currentSnapshot
    ? [
        { label: "VGV Contratado", value: formatCurrency(currentSnapshot.vgv), variant: "royal", icon: AttachMoneyRounded },
        ...(!isTodosEmpreendimentos
          ? [{
              label: "% POC - Percentage of Completion",
              value: formatPercent(currentSnapshot.poc, { decimals: 2 }),
              variant: "blue",
              icon: TrendingUpRounded,
            }]
          : []),
        { label: "% Vendido", value: formatPercent(currentSnapshot.pct_vendido, { decimals: 2 }), variant: "magenta", icon: BarChartRounded },
        ...(!isTodosEmpreendimentos
          ? [{
              label: "Margem Líquida na Viabilidade",
              value: selectedViabilidadeSimulation
                ? formatPercent(selectedViabilidadeSimulation.indicadores?.margemViabilidade, { decimals: 2 })
                : "Sem simulação",
              variant: "purple",
              icon: PercentRounded,
            }]
          : []),
        { label: "Receita Apropriada", value: formatCurrency(currentSnapshot.receita_apropriada), variant: "orange", icon: DescriptionOutlined },
        { label: "Custo Apropriado", value: formatCurrency(currentSnapshot.custo_apropriado), variant: "red", icon: TrendingUpRounded },
        { label: "Recebimentos Acum.", value: formatCurrency(currentSnapshot.recebimento_acumulado), variant: "teal", icon: AccountBalanceWalletOutlined },
        {
          label: "Tendência da Margem Líquida",
          value: formatPercent(tendenciaMargemLiquidaVisivel, { decimals: 2 }),
          variant: "green",
          icon: TrendingDownRounded,
        },
      ]
    : [];


  const chartData = visibleTimeline.map((item) => ({
    ...item,
    poc: item.poc,
    pct_vendido: item.pct_vendido,
    vgv: item.vgv / 1000000,
    receita_apropriada: item.receita_apropriada / 1000000,
    custo_apropriado: item.custo_apropriado / 1000000,
    recebimento_acumulado: item.recebimento_acumulado / 1000000,
    tendencia_margem_liquida: item.tendencia_margem_liquida,
  }));

  const isLoadingVisaoGeral = (apiStatus === "idle" || apiStatus === "loading") && !usingApiData;

  return (
    <>
      <AgenteContabilidadeShell
        title="Visão Geral"
        subtitle="Operação - Análise consolidada de todos os empreendimentos"
        updating={apiStatus === "loading" && usingApiData}
        agenteContext={{
          paginaAtual: "visao-geral",
          empresa: selectedEmpresa,
          competencia: activeMonth,
          dadosVisiveis: currentSnapshot
            ? {
                margemLiquidaNaViabilidade: selectedViabilidadeSimulation?.indicadores?.margemViabilidade ?? null,
                margemLiquidaNaViabilidadeStatus: selectedViabilidadeSimulation ? "Simulação selecionada" : "Sem simulação",
                poc: currentSnapshot.poc,
                percentualVendido: currentSnapshot.pct_vendido,
                vgvContratado: currentSnapshot.vgv,
                receitaApropriada: currentSnapshot.receita_apropriada,
                custoApropriado: currentSnapshot.custo_apropriado,
                recebimentoAcumulado: currentSnapshot.recebimento_acumulado,
                tendenciaMargemLiquida: tendenciaMargemLiquidaVisivel,
                empresaSelecionada: selectedEmpreendimentoContext,
              }
            : {},
        }}
        actions={[
          <FilterSelect
            key="all"
            value={selectedEmpresa}
            onChange={handleEmpresaChange}
            options={empresaOptions}
            icon={BusinessRounded}
            minWidth={300}
          />,
        ]}
      >
        {isLoadingVisaoGeral ? (
          <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
            <Stack alignItems="center" spacing={1.5} sx={{ textAlign: "center" }}>
              <CircularProgress size={34} />
              <Typography sx={{ fontWeight: 900, color: colors.text }}>
                Carregando dados da visao geral
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 13 }}>
                Aguarde enquanto consultamos os indicadores calculados pela API.
              </Typography>
            </Stack>
          </Paper>
        ) : !visibleTimeline.length ? (
          <Paper elevation={0} sx={{ borderRadius: 3, bgcolor: colors.surface, p: 4, border: `1px solid ${colors.border}` }}>
            <Stack alignItems="center" spacing={1.2} sx={{ textAlign: "center" }}>
              <Typography sx={{ fontWeight: 900, color: colors.text, fontSize: 20 }}>
                Nenhum dado disponível
              </Typography>
              <Typography sx={{ color: colors.muted, fontSize: 14, maxWidth: 520 }}>
                Não encontramos dados disponíveis para exibição no momento.
              </Typography>
            </Stack>
          </Paper>
        ) : (
          <>
            <PeriodSelector months={months} value={activeMonth} onChange={setSelectedMonth} />
            <MonthFallbackNotice requestedMonth={selectedMonth} activeMonth={activeMonth} />
            <EmpreendimentosIntersecaoNotice
              isTodosEmpreendimentos={isTodosEmpreendimentos}
              activeMonth={activeMonth}
              empreendimentos={intersecaoEmpreendimentos}
            />
            {!isTodosEmpreendimentos && !selectedViabilidadeSimulation ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Selecione ou crie uma simulação da DRE de Viabilidade para exibir as margens planejadas.
              </Alert>
            ) : null}
            <KpiGrid metrics={overviewMetrics} />
            <ChartCard
              title="Tendências Mensais"
              data={chartData}
              height={320}
              headerGradient="green"
              viewTabs={[
                {
                  key: "financeiro",
                  label: "Receita x Custo",
                  series: [
                    { key: "vgv", name: "VGV Contratado", color: "#6366f1" },
                    { key: "receita_apropriada", name: "Receita Apropriada", color: "#10b981" },
                    { key: "custo_apropriado", name: "Custo Apropriado", color: "#f43f5e" },
                    { key: "recebimento_acumulado", name: "Recebimento", color: "#6366f1" },
                  ],
                },
                {
                  key: "indicadores",
                  label: "Indicadores %",
                  percentMode: true,
                  series: [
                    ...(!isTodosEmpreendimentos
                      ? [{ key: "poc", name: "% POC - Percentage of Completion", color: "#06b6d4" }]
                      : []),
                    { key: "pct_vendido", name: "% Vendido", color: "#c026d3" },
                    { key: "tendencia_margem_liquida", name: "Tendencia da Margem Liquida", color: "#059669" },
                  ],
                },
              ]}
              defaultView="financeiro"
            />
          </>
        )}
      </AgenteContabilidadeShell>

    </>
  );
}
